import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useMemo } from 'react';
import { MinimumFeesFilters, MinimumFeeStatus } from '../models/MinimumFee';
import { useMinimumFees } from './useMinimumFees';

export interface MinimumFeesFilterState {
  team_id: string;
  year: string;
  month: string;
  status: string;
  invoice_id: string;
}

const parseOptionalNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const isMinimumFeeStatus = (value: string): value is MinimumFeeStatus =>
  value === 'pending' || value === 'not_required' || value === 'invoiced';

export const useMinimumFeesMain = () => {
  const { params: queryParams, updateParams } = useQueryParams({
    team_id: { type: 'string', default: '' },
    year: { type: 'string', default: '' },
    month: { type: 'string', default: '' },
    status: { type: 'string', default: '' },
    invoice_id: { type: 'string', default: '' },
  });

  const filtersState = useMemo<MinimumFeesFilterState>(
    () => ({
      team_id: queryParams.team_id ?? '',
      year: queryParams.year ?? '',
      month: queryParams.month ?? '',
      status: queryParams.status ?? '',
      invoice_id: queryParams.invoice_id ?? '',
    }),
    [queryParams.invoice_id, queryParams.month, queryParams.status, queryParams.team_id, queryParams.year],
  );

  const requestFilters = useMemo<Omit<MinimumFeesFilters, 'page' | 'per_page'>>(
    () => ({
      team_id: filtersState.team_id || undefined,
      year: parseOptionalNumber(filtersState.year),
      month: parseOptionalNumber(filtersState.month),
      status: isMinimumFeeStatus(filtersState.status) ? filtersState.status : undefined,
      invoice_id: filtersState.invoice_id || undefined,
    }),
    [filtersState.invoice_id, filtersState.month, filtersState.status, filtersState.team_id, filtersState.year],
  );

  const query = useMinimumFees(requestFilters);

  const handleFiltersChange = useCallback(
    (nextFilters: Partial<MinimumFeesFilterState>) => {
      updateParams(nextFilters);
    },
    [updateParams],
  );

  const clearFilters = useCallback(() => {
    updateParams({
      team_id: '',
      year: '',
      month: '',
      status: '',
      invoice_id: '',
    });
  }, [updateParams]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filtersState.team_id ||
          filtersState.year ||
          filtersState.month ||
          filtersState.status ||
          filtersState.invoice_id,
      ),
    [filtersState.invoice_id, filtersState.month, filtersState.status, filtersState.team_id, filtersState.year],
  );

  return {
    filtersState,
    hasActiveFilters,
    handleFiltersChange,
    clearFilters,
    data: query.data,
    meta: query.meta,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    error: query.error,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
  };
};
